import {
  isDimension,
  isEntity,
  isGroup,
  type DimensionNode,
  type EntityNode,
  type LayerId,
  type NodeId,
  type SceneNode,
} from './node';
import type { Style } from './style';
import { DEFAULT_DISPLAY, type LengthDisplay } from './units';

export interface Variable {
  readonly id: string;
  readonly name: string;
  readonly expr: string;
  readonly description?: string;
}

export interface Layer {
  readonly id: LayerId;
  readonly name: string;
  readonly visible: boolean;
  readonly locked: boolean;
  /** What every entity on the layer looks like, unless it sets its own. */
  readonly style?: Style;
}

/** DXF's default layer name, so a drawing with one layer exports the way CAD tools expect. */
export const DEFAULT_LAYER: Layer = { id: 'layer-1', name: '0', visible: true, locked: false };

export interface DrawingDocument {
  readonly name: string;
  readonly nodes: Readonly<Record<NodeId, SceneNode>>;
  readonly rootIds: readonly NodeId[];
  readonly variables: readonly Variable[];
  /** Never empty; every entity is on exactly one of them. */
  readonly layers: readonly Layer[];
  /** Where new entities go. */
  readonly activeLayerId: LayerId;
  /** What the numbers mean and how lengths are shown, from the drawing's settings. */
  readonly display: LengthDisplay;
}

export class DocumentError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DocumentError';
  }
}

export function emptyDocument(name = 'Untitled'): DrawingDocument {
  return {
    name,
    nodes: {},
    rootIds: [],
    variables: [],
    layers: [DEFAULT_LAYER],
    activeLayerId: DEFAULT_LAYER.id,
    display: DEFAULT_DISPLAY,
  };
}

export function getNode(doc: DrawingDocument, id: NodeId): SceneNode | undefined {
  return doc.nodes[id];
}

export function requireNode(doc: DrawingDocument, id: NodeId): SceneNode {
  const node = doc.nodes[id];
  if (!node) throw new DocumentError(`No node with id "${id}"`);
  return node;
}

export function childIdsOf(doc: DrawingDocument, parentId: NodeId | null): readonly NodeId[] {
  if (parentId === null) return doc.rootIds;
  const parent = requireNode(doc, parentId);
  if (!isGroup(parent)) throw new DocumentError(`Node "${parentId}" cannot hold children`);
  return parent.childIds;
}

export interface WalkEntry {
  readonly node: SceneNode;
  readonly depth: number;
  readonly index: number;
  readonly ancestors: readonly NodeId[];
}

export function* walk(
  doc: DrawingDocument,
  parentId: NodeId | null = null,
  depth = 0,
  ancestors: readonly NodeId[] = [],
): Generator<WalkEntry> {
  const ids = childIdsOf(doc, parentId);
  for (const [index, id] of ids.entries()) {
    const node = doc.nodes[id];
    if (!node) continue;
    yield { node, depth, index, ancestors };
    if (isGroup(node)) yield* walk(doc, id, depth + 1, [...ancestors, id]);
  }
}

export function allNodes(doc: DrawingDocument): SceneNode[] {
  return [...walk(doc)].map((entry) => entry.node);
}

export function entityNodes(doc: DrawingDocument): EntityNode[] {
  return allNodes(doc).filter(isEntity);
}

export function dimensionNodes(doc: DrawingDocument): DimensionNode[] {
  return allNodes(doc).filter(isDimension);
}

export function ancestorsOf(doc: DrawingDocument, id: NodeId): NodeId[] {
  const chain: NodeId[] = [];
  let current = doc.nodes[id]?.parentId ?? null;
  while (current !== null) {
    chain.push(current);
    current = doc.nodes[current]?.parentId ?? null;
  }
  return chain;
}

export function descendantsOf(doc: DrawingDocument, id: NodeId): NodeId[] {
  const node = doc.nodes[id];
  if (!node || !isGroup(node)) return [];
  return node.childIds.flatMap((childId) => [childId, ...descendantsOf(doc, childId)]);
}

export function isEffectivelyVisible(doc: DrawingDocument, id: NodeId): boolean {
  const node = doc.nodes[id];
  if (!node || !node.visible) return false;
  return ancestorsOf(doc, id).every((ancestorId) => doc.nodes[ancestorId]?.visible ?? true);
}
